import { useMemo } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Dot } from '@givebutter/primitives/dot';
import { maskDecimal } from '@givebutter/util';
import { faCheckCircle, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCampaignItems } from 'api/campaigns';
import * as AuctionTooltip from 'components/pages/campaign-settings/campaign-auction/AuctionTooltip';
import { ItemOptionsDropdown } from 'components/pages/campaign-settings/campaign-auction/ItemOptionsDropdown';
import { useCampaignItemRoutes } from 'components/pages/campaign-settings/campaign-auction/useCampaignItemRoutes';
import { getFormattedItemStatus } from 'components/pages/campaign-settings/campaign-auction/util';
import { TableConstants, sortQuery, useReactTableState } from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { TruncatedText } from 'components/truncated-text';
import { EmptyData } from 'components/util';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { isQueryKeyIndexMeta } from 'helpers/reactQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { AuctionItemStatus, CampaignItem } from 'types/domain';

const columnHelper = createColumnHelper<CampaignItem>();

const useItemsIndex = () => {
    const queryClient = useQueryClient();
    const currencySymbol = useCurrencySymbol();
    const { auctionsItemWinner = false } = useFlags();
    const { pagination, setPagination, sorting, setSorting } = useReactTableState();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;
    const auctionId = campaign.auction_id!;
    const itemRoutes = useCampaignItemRoutes();

    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search);

    const [isPending, params] = useIsPending(
        {
            auction_id: auctionId,
            page: pagination.pageIndex + 1,
            query: searchTerm ?? '',
            include: 'category',
            ...sortQuery(sorting),
        },
        500,
    );

    const { data: items } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemsFiltered(campaignId, params),
        queryFn: () => fetchCampaignItems<CampaignItem>({ campaignId, ...params }),
        staleTime: 0,
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: 'Item name',
                cell: info => {
                    const sponsor = info.row.original.sponsor_item?.sponsor;
                    return (
                        <div className={'relative flex items-center gap-1 font-medium'}>
                            <div>
                                <div className="flex max-w-[15rem]">
                                    <TruncatedText>{info.getValue()}</TruncatedText>
                                </div>
                                {!!sponsor && (
                                    <p className="block w-full text-xs italic text-gray-500">
                                        Sponsored by {sponsor?.name}
                                    </p>
                                )}
                            </div>
                            {info.row.original.visibility === 'featured' && (
                                <div
                                    className={
                                        'flex items-center gap-x-2 rounded-full bg-gray-100 px-2'
                                    }
                                >
                                    <p>Featured</p>
                                </div>
                            )}
                            {info.row.original.visibility === 'private' && (
                                <div
                                    className={
                                        'flex items-center gap-x-2 rounded-full bg-gray-100 px-2'
                                    }
                                >
                                    <FontAwesomeIcon icon={faEyeSlash} />
                                    <p>Private</p>
                                </div>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('id', {
                header: 'Item #',
                enableSorting: false,
                cell: info => {
                    return (
                        <div className={'flex items-center gap-x-2'}>
                            <span>#{info.getValue()}</span>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('category.name', {
                id: 'category_name',
                header: 'Category',
                cell: info => {
                    const category = info.row.original.category;

                    return (
                        <div className="whitespace-nowrap">
                            {category ? (
                                <p>
                                    {category.icon ? `${category.icon} ` : ''}
                                    {category.name}
                                </p>
                            ) : (
                                <EmptyData />
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('dashboard_status', {
                id: 'status',
                header: 'Status',
                enableSorting: false,
                cell: info => (
                    <div className={'flex items-center gap-x-2'}>
                        <StatusIndicator status={info.getValue()} />
                        <span>{getFormattedItemStatus(info.getValue())}</span>
                    </div>
                ),
            }),
            columnHelper.accessor('bids_count', {
                header: 'Bids',
                cell: info => {
                    const isFixedPrice = !info.row.original.allows_bid;
                    const bidsCount = info.row.original.bids_count;
                    if (isFixedPrice) {
                        return <EmptyData />;
                    }
                    return <span className={'font-medium'}>{bidsCount || 0}</span>;
                },
            }),
            columnHelper.accessor('remaining', {
                header: 'QTY remaining',
                enableSorting: false,
                cell: info => {
                    return (
                        <div className={'flex items-center gap-x-2 font-medium'}>
                            <span>{info.getValue() || 0}</span>
                            <span>of</span>
                            <span>{info.row.original.total_quantity}</span>
                        </div>
                    );
                },
            }),
            columnHelper.display({
                header: 'Current price',
                cell: info => {
                    const item = info.row.original;
                    return (
                        <div className={'flex items-center gap-x-2'}>
                            <p className={'font-medium'}>
                                {currencySymbol}
                                {maskDecimal(currentPrice(item))}
                            </p>
                            <ReserveMet item={item} />
                        </div>
                    );
                },
                meta: {
                    sortType: 'numeric',
                },
            }),
            columnHelper.display({
                id: TableConstants.Actions,
                cell: info => (
                    <ItemOptionsDropdown
                        item={info.row.original}
                        onDelete={() =>
                            queryClient.invalidateQueries({
                                queryKey: campaignQueryKeys.items(campaignId),
                                type: 'all',
                                predicate: isQueryKeyIndexMeta,
                            })
                        }
                    />
                ),
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [currencySymbol, queryClient, campaignId],
    );

    const table = useReactTable({
        data: items?.data ?? [],
        rowCount: items?.meta.total,
        columns,
        pageCount: items?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        state: {
            pagination,
            columnVisibility: {
                remaining: auctionsItemWinner,
            },
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            onRowClick: row => {
                queryClient.invalidateQueries({
                    queryKey: campaignQueryKeys.item(campaignId, row.original.id),
                });
            },
            to: cell => itemRoutes.show(cell.row.original.id),
        },
    });

    useSyncPaginatedData(table, items);

    return {
        campaignId,
        table,
        search,
        setSearch,
        searchTerm,
        isPending,
    };
};

interface StatusIndicatorProps {
    className?: string;
    status: AuctionItemStatus;
}

const StatusIndicator = (props: StatusIndicatorProps) => {
    const { status, className } = props;
    switch (status) {
        case 'upcoming':
            return <Dot className={`${className} bg-yellow-500`} />;
        case 'live':
        case 'ended':
            return <Dot className={`${className} bg-green-500`} />;
        case 'paused':
            return <Dot className={`${className} bg-neutral-500`} />;
        case 'draft':
            return <Dot className={`${className} bg-gray-500`} />;
        case 'sold':
            return <div>🎉</div>;
        case 'unsold':
            return <Dot className={`${className} bg-red-500`} />;
        case 'canceled':
        case 'auction canceled':
            return <Dot className={`${className} bg-orange-500`} />;
        default:
            return null;
    }
};

const ReserveMet = ({ item }: { item: CampaignItem }) => {
    const currencySymbol = useCurrencySymbol();

    if (!reserveWasMet(item)) {
        return null;
    }

    return (
        <AuctionTooltip.Root>
            <Tooltip.Trigger tabIndex={0} asChild={true}>
                <FontAwesomeIcon className={'text-green-500'} icon={faCheckCircle} />
            </Tooltip.Trigger>
            <AuctionTooltip.Content>
                {`Reserve met (${currencySymbol}${item.reserve_price})`}
            </AuctionTooltip.Content>
        </AuctionTooltip.Root>
    );
};

const reserveWasMet = (item: CampaignItem): boolean => {
    return !!item.reserve_price && currentPrice(item) >= item.reserve_price && item.bids_count > 0;
};

const currentPrice = (item: CampaignItem): number => {
    return (item.allows_bid ? item.highest_bid?.amount || item.start_bid : item.price) ?? 0;
};

export { useItemsIndex, ReserveMet, StatusIndicator };
